/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';
import type {
  ArtifactEditImpact,
  ArtifactTypeInfo,
  ArtifactTypeName,
  ArtifactUsage,
  VisibilityImpact,
} from '../types/artifact-usage';
import type { ArtifactSteward } from '@artifact-stewardship';
import type { TransferImpactDto } from '@transfer-impact';
import type { ArtifactInheritance } from '../types/artifact-inheritance';

/**
 * **Questions that are the same for every artifact type** (`P4`; A2's one resource path, in the small).
 *
 * Deliberately not a method on `demandSourceService`. The reverse lookup is one question over one
 * graph — #1071 asks for exactly that, *not* a per-page one — and putting it on the demand-source
 * service is how the second copy gets written the day a strategy page wants the same panel.
 *
 * This is where A2's generic resource path starts. When the descriptor registry (#983) lands, the
 * per-type services collapse into this one; until then it holds only what is genuinely type-agnostic.
 */
export const artifactService = {
  /**
   * What points at this artifact, what it points at that is broken, and what was not searched.
   *
   * **Never falls back to an empty answer on failure.** An empty `usedBy` with a null coverage
   * statement is indistinguishable from *"nothing uses this"*, and that sentence in front of a
   * delete button is the one thing this must not be able to say by accident. The error propagates so
   * the caller renders a failure rather than a clearance.
   */
  async usage(type: ArtifactTypeName, id: string | number): Promise<ArtifactUsage> {
    const { data } = await api.get<ArtifactUsage>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/used-by`,
    );
    return {
      usedBy: data?.usedBy ?? [],
      hiddenCount: data?.hiddenCount ?? 0,
      dangling: data?.dangling ?? [],
      coverage: data?.coverage ?? {
        declaredTypes: [],
        undeclaredTypes: [],
        unreadableTypes: [],
        // A response that arrived without coverage is not a complete answer, and must not read as
        // one. Saying so is the honest default; `null` here would be a clearance we did not receive.
        statement: 'bidBrain could not confirm what this covers, so treat it as incomplete.',
      },
    };
  },

  /**
   * **What making this artifact public — or private — would disclose** (`#1124`).
   *
   * Asked before the change, not after: with a clone the operator deliberately copies values and
   * the record shows them as their author, while with inheritance they widen a child and the
   * parent's values go along **silently**. Nobody decides that the disclosure happens, and this is
   * what lets a surface say so first.
   *
   * **Errors propagate.** A failed impact read must not resolve to *"nothing to disclose"* — that
   * sentence in front of a publish button is the one thing this must not be able to say by
   * accident, exactly as with {@link usage}.
   */
  async visibilityImpact(
    type: ArtifactTypeName,
    id: string | number,
    to: 'public' | 'private',
  ): Promise<VisibilityImpact> {
    const { data } = await api.get<VisibilityImpact>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/visibility-impact`,
      { params: { to } },
    );
    return data;
  },

  /**
   * **What moving this artifact to a new steward would change** (`OT3`, slice `3d`, `#900`).
   *
   * **Errors propagate**, as with {@link visibilityImpact}: a failed read must not resolve to
   * *nothing changes* in front of a transfer button.
   */
  async transferImpact(
    type: ArtifactTypeName,
    id: string | number,
    to: ArtifactSteward,
    /** Describe it together with a change of reach the operator also chose (`OT4`). */
    andVisibility?: 'public' | 'private',
  ): Promise<TransferImpactDto> {
    const target = to.kind === 'user' ? { to: 'user', userId: to.userId } : { to: 'system' };
    const { data } = await api.get<TransferImpactDto>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/transfer-impact`,
      { params: andVisibility ? { ...target, andVisibility } : target },
    );
    return data;
  },

  /**
   * **Move an artifact to a new steward** (`OT1`) — the one way ownership changes, for every type.
   * An ordinary save leaves ownership as stored, so this is never folded into a form's save.
   */
  async transfer<T = unknown>(type: ArtifactTypeName, id: string | number, to: ArtifactSteward): Promise<T> {
    const { data } = await api.post<T>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/transfer`,
      { to },
    );
    return data;
  },

  /**
   * **Which of an artifact's values it states for itself, and which come from its parent** (`P3a`).
   * One route for every type since slice 7a; it was a demand-source method until request strategies
   * gained *extend*.
   *
   * **Paths, never values.** The resolved document is already what the page renders; what it cannot
   * say is where each leaf came from, and that is all this adds. Asking the server for the parent's
   * values instead would answer the same question twice and disclose a parent this viewer may not
   * be allowed to see — see {@link revert}.
   */
  async inheritance(type: ArtifactTypeName, id: string | number): Promise<ArtifactInheritance> {
    const { data } = await api.get<ArtifactInheritance>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/inheritance`,
    );
    return {
      parentId: data?.parentId ?? null,
      overriddenPaths: data?.overriddenPaths ?? [],
      inheritedPaths: data?.inheritedPaths ?? [],
      addedPaths: data?.addedPaths ?? [],
    };
  },

  /**
   * Give values back to the parent — **revert is a deletion** (`P3a`).
   *
   * Sends paths rather than the parent's values, which is what keeps it honest: removing the leaf
   * from this artifact's layer restores the parent's value by construction, so there is no second
   * value here that can be stale, wrong, or a disclosure. Returns what actually changed — a path
   * that was already inherited is a no-op, not an error.
   */
  async revert(type: ArtifactTypeName, id: string | number, paths: string[]): Promise<string[]> {
    const { data } = await api.post<{ reverted?: string[] }>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/revert`,
      { paths },
    );
    return data?.reverted ?? [];
  },

  /**
   * **What saving this would change for the sources built from it** (`P4` slice 2).
   *
   * A `POST` because it carries the proposed document — the server differences it against the parent
   * through the same path a save takes, so the preview describes what storage will actually serve
   * rather than what the caller happens to be holding.
   *
   * **Errors propagate.** A caller that fell back to an empty impact would render *"nothing else is
   * affected"* over a question that failed, in front of a Save button. The guard turns a failure
   * into *"could not check — save anyway?"*, which is a different sentence and the honest one.
   */
  async editImpact(type: ArtifactTypeName, id: string | number, proposed: object): Promise<ArtifactEditImpact> {
    const { data } = await api.post<ArtifactEditImpact>(
      `/api/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}/edit-impact`,
      proposed,
    );
    return {
      touchedPaths: data?.touchedPaths ?? [],
      unknown: data?.unknown ?? false,
      children: data?.children ?? [],
      hiddenCount: data?.hiddenCount ?? 0,
    };
  },

  /**
   * **The artifact-type vocabulary** — what types exist and what an operator calls one (`A1`).
   *
   * Read once and cached for the session by `useArtifactTypes`: it changes when a release changes
   * it, which is the *slow-changing data* rule's own case for holding rather than re-reading.
   */
  async types(): Promise<ArtifactTypeInfo[]> {
    const { data } = await api.get<ArtifactTypeInfo[]>('/api/artifacts/types');
    return data ?? [];
  },
};
